import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Role, RoleColor, Color, DASHBOARD_ROLES } from '../constants';
import API from '../utils/api';

const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: '/',
    end: true,
    roles: DASHBOARD_ROLES,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    label: 'Work Orders',
    path: '/work-orders',
    end: true,
    roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.GSM, Role.DSM, Role.ASM, Role.SA, Role.ST],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    label: 'Create Work Order',
    path: '/work-orders/new',
    roles: [Role.SUPER_ADMIN, Role.GSM, Role.DSM, Role.ASM, Role.SA],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
  },
  {
    label: 'Admin Panel',
    path: '/admin',
    end: true,
    adminOnly: true,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    label: 'Delegations',
    path: '/delegations',
    end: true,
    roles: [Role.DSM],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    label: 'System Logs',
    path: '/admin/logs',
    roles: [Role.SUPER_ADMIN],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 17v-2m3 2v-4m3 4v-6M3 20h18M5 20V8l7-5 7 5v12" />
      </svg>
    ),
  },
];

export default function Layout() {
  const { user, logout, isDelegated, delegation, isAdminPanel } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try { await API.post('/auth/logout'); } catch {}
    logout();
    navigate('/login');
  };

  const roleStyle = RoleColor[user?.role] || { bg: '#F3F4F6', text: '#666666' };

  const visibleNav = NAV_ITEMS.filter(item => {
    if (item.adminOnly) return isAdminPanel();
    return item.roles?.includes(user?.role);
  });

  const SidebarContent = () => (
    <div className="flex flex-col h-full">

      {/* Logo */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: Color.NAVY }}>
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <span className="font-bold text-gray-800 text-lg">Work Orders</span>
        </div>
      </div>

      {/* Delegation banner */}
      {isDelegated() && delegation && (
        <div className="mx-3 mt-3 p-2 rounded-lg text-xs"
          style={{ backgroundColor: '#FFF8E1', color: '#B38600', border: '1px solid #FFBE00' }}>
          <p className="font-semibold">⚡ Temporary Manager Access</p>
          <p className="mt-0.5 text-gray-500">
            Expires {new Date(delegation.expires_at).toLocaleDateString()}
          </p>
        </div>
      )}

      {/* User info */}
      <div className="p-4 border-b border-gray-100">
        <p className="font-medium text-gray-800 text-sm truncate">{user?.name}</p>
        <span className="text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block"
          style={{ backgroundColor: roleStyle.bg, color: roleStyle.text }}>
          {user?.role}
        </span>
        {user?.route && (
          <p className="text-xs text-gray-400 mt-1">Route: {user.route}</p>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5">
        {visibleNav.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }`
            }
            style={({ isActive }) => isActive ? { backgroundColor: Color.NAVY } : {}}
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Sign out */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                     font-medium text-gray-600 hover:bg-red-50 hover:text-red-600
                     transition-colors w-full"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200 fixed h-full"
        style={{ zIndex: 1100 }}
      >
        <SidebarContent />
      </aside>

      {/*
        Mobile overlay.

        Layering note: Leaflet gives its own panes and controls z-index 400 to
        1000, so anything that must sit above the map needs to clear 1000.
        The scale used here is: map 0-1000, page chrome 1100,
        filter sheet 2000, navigation drawer 3000.
      */}
      {sidebarOpen && (
        <div className="fixed inset-0 md:hidden" style={{ zIndex: 3000 }}>
          <div className="absolute inset-0 bg-black opacity-40"
            onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">

        {/* Top bar */}
        <header
          className="bg-white border-b border-gray-200 px-4 py-3
                     flex items-center justify-between sticky top-0"
          style={{ zIndex: 1100 }}
        >
          <button
            className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            onClick={() => setSidebarOpen(true)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="md:hidden font-bold text-gray-800">Work Orders</div>
          <div className="hidden md:block" />
          <div className="text-sm text-gray-500">
            Hi, <span className="font-medium text-gray-700">
              {user?.name?.split(' ')[0]}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>

      </div>
    </div>
  );
}